import React, { useMemo } from 'react'

import { APIRequest } from 'models/API'
import { APIRequestState } from 'constants/API'
import Big from 'big.js'
import { ProductKindDTO } from 'models/product'
import { UpsellPayment } from 'components/pages/Gallery/common/UpsellPayment'
import { ZERO_DECIMAL } from 'constants/misc'
import { decimalFromBig } from 'utils/price'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'

const productCount = 1 // Defaulting to 1 for request change as it is a single purchase

/**
 * @interface Props for the AdditionalChangeUpsellPayment component.
 */
interface Props {
  /** Current selected assignmentId */
  assignmentId: string
  /** The product kind data transfer object. */
  product: ProductKindDTO
  /** The API request for the additional change request. */
  additionalChangeRequest: APIRequest<unknown>
  /** The state of the additional change request. */
  additionalChangeRequestState: APIRequestState
  /** The price for each additional change. */
  pricePerAdditionalChange: Big
  /** Whether the additional change purchase modal is open. */
  isAdditionalChangePurchaseModalOpen: boolean
  /** Function to set the open state of the additional change purchase modal. */
  setAdditionalChangePurchaseModalOpen: (open: boolean) => void
  /** Function to submit the additional change request. */
  submitAdditionalChangeRequest: (paymentIntentId?: string, billingProductId?: number) => void
}

/**
 * @component
 * AdditionalChangeUpsellPayment displays the payment modal for purchasing additional change requests as an upsell within the client gallery.
 * 
 * @example
 * <AdditionalChangeUpsellPayment
 *  assignmentId="12345"
 *  product={productData} 
 *  additionalChangeRequest={productRequestData}
 *  additionalChangeRequestState={APIRequestState.OK}
 *  pricePerAdditionalChange={new Big(10)}
 *  isAdditionalChangePurchaseModalOpen={isModalOpen}
 *  setAdditionalChangePurchaseModalOpen={setModalOpen}
 *  submitAdditionalChangeRequest={handleSubmitAdditionalChangeRequest}
 * />
 */
export const AdditionalChangeUpsellPayment: React.FC<Props> = ({
  assignmentId,
  product,
  additionalChangeRequest,
  additionalChangeRequestState,
  pricePerAdditionalChange,
  isAdditionalChangePurchaseModalOpen,
  setAdditionalChangePurchaseModalOpen,
  submitAdditionalChangeRequest,
}) => {
  const { t } = useTranslation(['gallery'])

  const { spawnSuccessToast } = useSnackbar()
  const currency = useMemo(() => product?.feePrice.currency, [product?.feePrice.currency])

  const handleOnModalClose = () => {
    setAdditionalChangePurchaseModalOpen(false)
  }

  return (
    <UpsellPayment
      isOpen={isAdditionalChangePurchaseModalOpen}
      assignmentId={assignmentId}
      productId={product?.id}
      productQuantity={productCount}
      total={
        !!currency
          ? {
            value: decimalFromBig(pricePerAdditionalChange),
            currency,
            discountPercentage: product?.feePrice.discountPercentage || ZERO_DECIMAL,
          }
          : null
      }
      beforeConfirmAction={(paymentIntentId, billingProductId) => {
        submitAdditionalChangeRequest(paymentIntentId, billingProductId)
      }}
      beforeConfirmActionState={additionalChangeRequestState}
      beforeConfirmationActionRequest={additionalChangeRequest}
      onClose={handleOnModalClose}
      afterClosed={handleOnModalClose}
      onSuccess={() => {
        spawnSuccessToast(t('documents_upsell.success_text'))
      }}
    >
    </UpsellPayment>
  )
}
